import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { defineFormPage, useFormPage } from '@repo/form';
import { FormActions } from '@repo/web-form';
import { FormPageLayout } from '../../../../components/layout/FormPageLayout';
import { Input, FormField } from '../../../../components/ui/FormField';
import { FormSelect } from '../../../../components/ui/FormSelect';
import { useToast } from '../../../../components/ui/Toast';
import { Decimal } from 'decimal.js';
import { useCreateChitActFee } from '../../../../features/finance/hooks';
import { SchemeSearchInput } from '../../../../features/finance/components/SchemeSearchInput';
import type { AnyFieldApi } from '@tanstack/react-form';
import { z } from 'zod';
import { Banner } from '../../../../components/ui/Banner';

export const Route = createFileRoute('/_auth/finance/chit-act-fees/new')({
  component: LogChitActFeePage,
});

const FEE_TYPE_LABELS: Record<string, string> = {
  BYLAW_REGISTRATION: 'Bylaw Registration Fee (Sec 4)',
  MINUTES_FILING: 'Minutes of Meeting Filing Fee (Sec 38)',
  DECLARATION_COMMENCEMENT: 'Declaration of Commencement (Sec 9)',
  AUDITED_BALANCE_SHEET_FILING: 'Audited Balance Sheet Filing (Sec 24)',
};

const feeSchema = z.object({
  schemeId: z.string().min(1, 'Chit scheme is required'),
  feeType: z.string().min(1, 'Fee type is required'),
  rupeesAmount: z.string().min(1, 'Amount is required'),
  challanNumber: z.string().min(1, 'Challan number is required'),
  paymentDate: z.string().min(1, 'Payment date is required'),
  notes: z.string().max(500).optional(),
});

type FeeForm = z.infer<typeof feeSchema>;

const feeFormConfig = defineFormPage<FeeForm>({
  resource: 'chit-act-fees',
  defaultValues: {
    schemeId: '',
    feeType: 'BYLAW_REGISTRATION',
    rupeesAmount: '',
    challanNumber: '',
    paymentDate: new Date().toISOString().split('T')[0],
    notes: '',
  },
  schema: feeSchema,
});

function LogChitActFeePage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const createMutation = useCreateChitActFee();

  const {
    form,
    isSubmitting,
    handleSubmit,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(feeFormConfig, createMutation, {
    routeId: 'finance/chit-act-fees/new',
    onBeforeSubmit: (value) => {
      const rupees = new Decimal(value.rupeesAmount);
      if (rupees.isNaN() || rupees.lte(0)) throw new Error('Please enter a valid amount.');
      return {
        schemeId: value.schemeId,
        feeType: value.feeType,
        challanNumber: value.challanNumber.toUpperCase(),
        amountPaise: rupees.times(100).round().toNumber(),
        dueDate: value.paymentDate,
        notes: value.notes || undefined,
       };
    },
    onSuccess: () => {
      toast({
        type: 'success',
        title: 'Challan Fee Logged',
        description: 'Chit Funds Act filing fee and government challan recorded for audit alignment.',
      });
      void navigate({ to: '/finance/chit-act-fees' });
    },
    onError: (err: { message?: string }) => {
      toast({
        type: 'error',
        title: 'Failed to Log Challan',
        description: err?.message || 'Could not log the filing challan. Please try again.',
      });
    },
  });

  return (
    <FormPageLayout
      title="Record Chit Act Fee"
      subtitle="Record statutory chit act registration fees paid to the state Registrar of Chits for the current financial period."
      backTo="/finance/chit-act-fees"
      variant="card"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
            {hasDraft && (
              <Banner
                variant="info"
                title="Restore Draft"
                description="You have an unsaved draft from a previous session. Would you like to restore it?"
                action={{
                  label: 'Restore',
                  onClick: () => {
                    const draft = restoreDraft();
                    if (draft) {
                      Object.entries(draft).forEach(([key, val]) => {
                        form.setFieldValue(key as never, val as never);
                      });
                    }
                    dismissDraft();
                  },
                }}
                secondaryAction={{
                  label: 'Discard',
                  onClick: () => {
                    clearDraft();
                  },
                }}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              <form.Field
                name="schemeId"
                validators={{ onChange: ({ value }: { value: unknown }) => feeSchema.shape.schemeId.safeParse(value).success ? undefined : feeSchema.shape.schemeId.safeParse(value).error?.issues[0]?.message }}
                children={(field: AnyFieldApi) => (
                  <FormField label="Target Chit Scheme" required>
                    <SchemeSearchInput
                      value={field.state.value}
                      onChange={(schemeId: string) => field.handleChange(schemeId)}
                      disabled={isSubmitting}
                    />
                  </FormField>
                )}
              />

              <form.Field
                name="challanNumber"
                validators={{ onChange: ({ value }: { value: unknown }) => feeSchema.shape.challanNumber.safeParse(value).success ? undefined : feeSchema.shape.challanNumber.safeParse(value).error?.issues[0]?.message }}
                children={(field: AnyFieldApi) => (
                  <FormField label="Statutory Challan GRN/No." required>
                    <Input
                      placeholder="e.g. CHLN-BYL-88192"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </FormField>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <form.Field
                name="feeType"
                children={(field: AnyFieldApi) => (
                  <FormSelect
                    label="Filing Type (Statutory Sec)"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => field.handleChange(e.target.value)}
                    options={[
                      { value: 'BYLAW_REGISTRATION', label: FEE_TYPE_LABELS.BYLAW_REGISTRATION },
                      { value: 'DECLARATION_COMMENCEMENT', label: FEE_TYPE_LABELS.DECLARATION_COMMENCEMENT },
                      { value: 'MINUTES_FILING', label: FEE_TYPE_LABELS.MINUTES_FILING },
                      { value: 'AUDITED_BALANCE_SHEET_FILING', label: FEE_TYPE_LABELS.AUDITED_BALANCE_SHEET_FILING },
                    ]}
                    disabled={isSubmitting}
                  />
                )}
              />

              <form.Field
                name="rupeesAmount"
                validators={{ onChange: ({ value }: { value: unknown }) => feeSchema.shape.rupeesAmount.safeParse(value).success ? undefined : feeSchema.shape.rupeesAmount.safeParse(value).error?.issues[0]?.message }}
                children={(field: AnyFieldApi) => (
                  <FormField label="Amount Paid" required>
                    <Input
                      type="number"
                      placeholder="e.g. 1500"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </FormField>
                )}
              />
            </div>

            <form.Field
              name="paymentDate"
              validators={{ onChange: ({ value }: { value: unknown }) => feeSchema.shape.paymentDate.safeParse(value).success ? undefined : feeSchema.shape.paymentDate.safeParse(value).error?.issues[0]?.message }}
              children={(field: AnyFieldApi) => (
                <FormField label="Filing Payment Date" required>
                    <Input
                      type="date"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </FormField>
                )}
              />

             <form.Field
              name="notes"
              children={(field: AnyFieldApi) => (
                <FormField label="Additional Notes">
                  <textarea
                    className="w-full h-24 p-3 border border-border rounded-lg bg-surface-2 text-sm focus:outline-none focus:ring-1 focus:ring-accent-500"
                    placeholder="Enter additional filing details or notes here..."
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => field.handleChange(e.target.value)}
                    disabled={isSubmitting}
                  />
                </FormField>
              )}
            />

            <FormActions onCancel={() => void navigate({ to: '/finance/chit-act-fees' })} isSubmitting={isSubmitting} submitLabel="Save Challan Log" />
          </form>
    </FormPageLayout>
  );
}
